import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Facebook, CheckCircle, AlertCircle, RefreshCw, Zap, Copy, Key, Shield, Building2 } from 'lucide-react';
import {
  fetchFacebookAccounts, startConnect, fetchPendingPages, finalizeConnect, disconnectAccount, clearConnectUrl,
  fetchUrls, fetchAppCredentials, saveAppCredentials, connectWithAccessToken,
} from '../../redux/facebookSlice';
import alertService from '../../utils/alert';

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[#E5E5E5]">
        <Icon size={16} className="text-[#1877F2]" />
        <span className="text-[13px] font-bold text-[#2E2E2E]">{title}</span>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-semibold text-[#706E6B] block">{label}</label>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-[12px] font-mono text-[#0176D3] bg-[#F4F6F9] border border-[#E5E5E5] rounded-[4px] px-3 py-2 break-all">
          {value}
        </code>
        <button onClick={copy} className="flex-shrink-0 p-2 border border-[#E5E5E5] hover:border-[#0176D3] rounded-[4px] text-[#5C5C5C] hover:text-[#0176D3]">
          <Copy size={14} />
        </button>
      </div>
      {copied && <p className="text-[11px] text-[#2E844A]">Copied!</p>}
    </div>
  );
}

const INPUT = 'w-full border border-[#E5E5E5] rounded-[4px] px-3 py-2 text-[13px] text-[#2E2E2E] focus:outline-none focus:border-[#0176D3]';

function randomToken() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16))).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// What each permission is for — a Page missing one of these can't do that feature, and Facebook
// answers with error (#10) "requires the 'pages_read_engagement' permission" (e.g. comment sync).
// Mirrors REQUESTED_PERMISSIONS in src/modules/meta-facebook/services/FacebookOAuthService.js.
const FACEBOOK_PERMISSIONS = [
  ['pages_show_list', 'See your Pages'],
  ['pages_read_engagement', 'Read posts and comments'],
  ['pages_read_user_content', "Read visitors' comments"],
  ['pages_manage_engagement', 'Reply to, hide and delete comments'],
  ['pages_manage_posts', 'Publish posts'],
  ['pages_messaging', 'Messenger inbox'],
  ['pages_manage_metadata', 'Receive new comments and messages live'],
  ['business_management', 'Find Pages in a Business Portfolio'],
];

function PermissionStatus({ granted }) {
  if (!granted.length) {
    return (
      <p className="text-[12px] text-[#5C5C5C] bg-[#F4F6F9] border border-[#D8DDE6] rounded-[4px] px-3 py-2">
        Permissions weren't recorded for this connection. Reconnect the Page below to see which ones Facebook granted.
      </p>
    );
  }
  const have = new Set(granted);
  const missing = FACEBOOK_PERMISSIONS.filter(([key]) => !have.has(key));
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold text-[#706E6B]">Permissions Facebook granted</p>
      <div className="flex flex-wrap gap-1.5">
        {FACEBOOK_PERMISSIONS.map(([key, label]) => (
          <span key={key} title={key} className={`text-[11px] px-2 py-0.5 rounded-full border ${have.has(key) ? 'bg-[#E3F3E8] border-[#9FD9B4] text-[#036B3A]' : 'bg-[#FDE7E9] border-[#F6A3AB] text-[#8E030F]'}`}>
            {have.has(key) ? '✓' : '✗'} {label}
          </span>
        ))}
      </div>
      {missing.length > 0 && (
        <p className="text-[12px] text-[#8E030F] bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-3 py-2">
          Missing: {missing.map(([key]) => key).join(', ')}. Anything that needs them (comment sync, replies, messaging) will fail with a Facebook permission error.
          Use <strong>Connect Another Page</strong> below, choose this Page and approve <em>every</em> permission in Facebook's dialog — don't untick any.
        </p>
      )}
    </div>
  );
}

export default function FacebookSettingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((s) => s.auth);
  const { accounts, accountLoading, pendingPages, connectUrl, urls, credential, error } = useSelector((s) => s.facebook);
  const [connecting, setConnecting] = useState(false);
  const [disconnectingPageId, setDisconnectingPageId] = useState(null);
  const [form, setForm] = useState({ appId: '', appSecret: '', webhookVerifyToken: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingSecret, setEditingSecret] = useState(false);
  const trim = (v) => (typeof v === 'string' ? v.trim() : v);

  const oauthError = searchParams.get('error');
  const pendingId = searchParams.get('pendingId');
  const justConnected = searchParams.get('connected');

  const load = useCallback(() => dispatch(fetchFacebookAccounts()), [dispatch]);
  const [resubscribingPageId, setResubscribingPageId] = useState(null);
  const resubscribe = async (pageId) => {
    setResubscribingPageId(pageId);
    try {
      await api.post(`/meta-facebook/pages/${pageId}/resubscribe`);
    } catch { /* the refreshed account shows the outcome either way */ }
    await load();
    setResubscribingPageId(null);
  };

  useEffect(() => { load(); dispatch(fetchUrls()); dispatch(fetchAppCredentials()); }, [load, dispatch]);

  // Prefill the form (except the secret, which the server never returns) once
  // credentials load; generate a fresh verify token for a brand-new setup.
  useEffect(() => {
    if (credential) {
      setForm({ appId: credential.appId || '', appSecret: '', webhookVerifyToken: credential.webhookVerifyToken || '' });
    } else {
      setForm((f) => (f.webhookVerifyToken ? f : { ...f, webhookVerifyToken: randomToken() }));
    }
  }, [credential]);

  useEffect(() => {
    if (pendingId) dispatch(fetchPendingPages(pendingId));
  }, [pendingId, dispatch]);

  useEffect(() => {
    if (justConnected) {
      load();
      setSearchParams({}, { replace: true });
    }
  }, [justConnected, load, setSearchParams]);

  useEffect(() => {
    if (connectUrl) {
      window.location.href = connectUrl;
      dispatch(clearConnectUrl());
    }
  }, [connectUrl, dispatch]);

  const saveCredentials = async () => {
    setSaving(true);
    const result = await dispatch(saveAppCredentials(form));
    setSaving(false);
    if (!result.error) {
      setSaved(true);
      setEditingSecret(false);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const connect = async () => {
    setConnecting(true);
    await dispatch(startConnect());
    setConnecting(false);
  };

  const [showTokenConnect, setShowTokenConnect] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [tokenConnecting, setTokenConnecting] = useState(false);

  const connectWithToken = async () => {
    if (!tokenInput.trim()) return;
    setTokenConnecting(true);
    const result = await dispatch(connectWithAccessToken(tokenInput.trim()));
    setTokenConnecting(false);
    if (!result.error) {
      setTokenInput('');
      setShowTokenConnect(false);
      if (result.payload?.status === 'choose_page') {
        setSearchParams({ pendingId: result.payload.pendingId }, { replace: true });
      } else {
        load();
      }
    }
  };

  // Every Page found is pre-selected — connecting all of them is the usual intent.
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [finalizing, setFinalizing] = useState(false);
  useEffect(() => { setSelectedPendingIds(pendingPages.map((p) => p.id)); }, [pendingPages]);
  const togglePending = (id) => setSelectedPendingIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const connectSelected = async () => {
    setFinalizing(true);
    const result = await dispatch(finalizeConnect({ pendingId, pageIds: selectedPendingIds }));
    setFinalizing(false);
    if (!result.error) {
      setSearchParams({}, { replace: true });
      load();
    }
  };

  const disconnect = async (pageId) => {
    if (!(await alertService.confirm('Disconnect?', 'Disconnect this Facebook Page? Messenger and comment moderation will stop working for it until you reconnect.', 'danger', 'Disconnect'))) return;
    setDisconnectingPageId(pageId);
    await dispatch(disconnectAccount(pageId));
    setDisconnectingPageId(null);
  };

  if (accountLoading && !accounts.length) {
    return <div className="py-10 px-4 text-[13px] text-[#5C5C5C]">Loading Facebook settings…</div>;
  }

  const hasCredential = !!credential?.appId;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 bg-[#F3F2F2] border border-[#E5E5E5] rounded-[4px] px-4 py-2.5">
        <Building2 size={14} className="text-[#5C5C5C] flex-shrink-0" />
        <div className="text-[12px] text-[#3E3E3C]">
          You're viewing Facebook Page settings for{' '}
          <span className="font-bold text-[#2E2E2E]">{user?.company || 'this account'}</span>
          {user?._id && <span className="text-[#5C5C5C]"> ({user._id})</span>}.
          {' '}App credentials and connections are saved per account — make sure this is the account you intend to connect before continuing.
        </div>
      </div>
      {oauthError && (
        <div className="flex items-start gap-3 bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-4 py-3">
          <AlertCircle size={16} className="text-[#BA0517] mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-[#8E030F]">{decodeURIComponent(oauthError)}</div>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-3 bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-4 py-3">
          <AlertCircle size={16} className="text-[#BA0517] mt-0.5 flex-shrink-0" />
          <div className="text-[13px] text-[#8E030F]">{error}</div>
        </div>
      )}

      {pendingId && pendingPages.length > 0 && (
        <Section title="Choose Facebook Pages" icon={Facebook}>
          <p className="text-[13px] text-[#5C5C5C]">
            {pendingPages.length} Facebook Pages were found. Choose which ones to connect — you can connect all of them at once.
          </p>
          <div className="space-y-2">
            {pendingPages.map((p) => (
              <label
                key={p.id}
                className="w-full flex items-center gap-3 border border-[#E5E5E5] hover:border-[#1877F2] rounded-[4px] px-4 py-3 cursor-pointer transition-colors"
              >
                <input type="checkbox" checked={selectedPendingIds.includes(p.id)} onChange={() => togglePending(p.id)} />
                <span className="text-[13px] font-semibold text-[#2E2E2E]">{p.name}</span>
              </label>
            ))}
          </div>
          <button
            onClick={connectSelected}
            disabled={finalizing || selectedPendingIds.length === 0}
            className="w-full bg-[#1877F2] hover:bg-[#1466D8] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2.5 rounded-[4px]"
          >
            {finalizing ? 'Connecting…' : `Connect ${selectedPendingIds.length} Page${selectedPendingIds.length === 1 ? '' : 's'}`}
          </button>
        </Section>
      )}

      {accounts.length > 0 && (
        <Section title={`Connected Pages (${accounts.length})`} icon={Facebook}>
          {accounts.map((account) => (
            <div key={account.pageId} className="border border-[#E5E5E5] rounded-[4px] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {account.profilePictureUrl && (
                    <img src={account.profilePictureUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                  )}
                  <div>
                    <div className="text-[13px] font-bold text-[#2E2E2E]">{account.pageName}</div>
                    <div className="text-[12px] text-[#5C5C5C]">
                      Facebook Page
                      {' · '}
                      <span className={account.status === 'active' ? 'text-[#2E844A]' : 'text-[#BA0517]'}>{account.status}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => disconnect(account.pageId)}
                  disabled={disconnectingPageId === account.pageId}
                  className="text-[13px] font-semibold text-[#BA0517] hover:text-[#8E030F] disabled:opacity-60"
                >
                  {disconnectingPageId === account.pageId ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>

              <PermissionStatus granted={account.permissions || []} />

              {account.reconnectRequired && (
                <div className="flex items-start gap-3 bg-[#FFF4E5] border border-[#FFB75D] rounded-[4px] px-4 py-3">
                  <RefreshCw size={16} className="text-[#DD7A01] mt-0.5 flex-shrink-0" />
                  <div className="text-[13px] text-[#8C4B02]">
                    <strong>Reconnect needed.</strong> Facebook couldn't silently refresh your access for this Page —
                    reconnect below to keep Messenger and comment moderation working.
                  </div>
                </div>
              )}

              {account.webhooksSubscribed === false && (
                <div className="flex items-start gap-3 bg-[#FDE7E9] border border-[#F6A3AB] rounded-[4px] px-4 py-3">
                  <AlertCircle size={16} className="text-[#BA0517] mt-0.5 flex-shrink-0" />
                  <div className="text-[13px] text-[#8E030F]">
                    <strong>Webhooks aren't active.</strong> Meta rejected the subscription for this Page, so new Messenger
                    messages and comments won't arrive automatically (existing posts/comments can still be pulled in with
                    "Sync Posts" on the Comments page). This is usually an app-configuration issue on Meta's side — check
                    that your Meta App's <strong>Webhooks</strong> product has a Page subscription for <code>messages</code> and{' '}
                    <code>feed</code>, and that your app has Advanced Access for <code>pages_messaging</code>.
                    {account.webhooksSubscribeError && (
                      <div className="mt-2 text-[12px]"><strong>Meta said:</strong> {account.webhooksSubscribeError}</div>
                    )}
                    <button
                      onClick={() => resubscribe(account.pageId)}
                      disabled={resubscribingPageId === account.pageId}
                      className="mt-2 block text-[12px] font-semibold text-[#0176D3] hover:underline disabled:opacity-50"
                    >
                      {resubscribingPageId === account.pageId ? 'Retrying…' : 'Retry subscription'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {!pendingId && (
          <Section title={accounts.length ? 'Connect Another Page' : 'Connect Facebook Page'} icon={Zap}>
            <p className="text-[13px] text-[#5C5C5C]">
              This CRM connects to Facebook using <strong>your own</strong> Meta Developer App — not a shared app used by
              every customer. If you haven't created one yet, go to{' '}
              <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="underline font-semibold text-[#0176D3]">
                developers.facebook.com/apps
              </a>{' '}
              and create one first.
            </p>
            <p className="text-[11px] text-[#5C5C5C]">
              Independent of Instagram — connecting a Page here works whether or not it has a linked Instagram account.
              In your app: add the Facebook Login and Messenger products.
            </p>

            {urls && (
              <div className="space-y-3 border-t border-[#E5E5E5] pt-4">
                <p className="text-[12px] font-bold text-[#2E2E2E]">1. Add these to your Meta App Dashboard</p>
                <CopyField label="OAuth Redirect URI" value={urls.callbackUrl} />
                <CopyField label="Webhook Callback URL" value={urls.webhookUrl} />
              </div>
            )}

            <div className="space-y-3 border-t border-[#E5E5E5] pt-4">
              <p className="text-[12px] font-bold text-[#2E2E2E]">2. Enter your app's credentials</p>
              <div>
                <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">App ID</label>
                <div className="relative">
                  <Key size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                  <input
                    className={`${INPUT} pl-9`}
                    value={form.appId}
                    onChange={(e) => setForm((f) => ({ ...f, appId: e.target.value }))}
                    onBlur={(e) => setForm((f) => ({ ...f, appId: trim(e.target.value) }))}
                    placeholder="990602627938098"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">App Secret</label>
                {hasCredential && !editingSecret ? (
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Shield size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                      <div className={`${INPUT} pl-9 bg-[#F4F6F9] text-[#5C5C5C] select-none`}>••••••••••••••••</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditingSecret(true)}
                      className="flex-shrink-0 text-[12px] font-semibold text-[#0176D3] hover:underline"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Shield size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
                    <input
                      type="password"
                      autoFocus={editingSecret}
                      className={`${INPUT} pl-9`}
                      value={form.appSecret}
                      onChange={(e) => setForm((f) => ({ ...f, appSecret: e.target.value }))}
                      onBlur={(e) => setForm((f) => ({ ...f, appSecret: trim(e.target.value) }))}
                      placeholder="your app secret"
                    />
                  </div>
                )}
                {hasCredential && (
                  <p className="text-[11px] text-[#5C5C5C] mt-1">
                    {editingSecret
                      ? "Rotated your App Secret on Meta's dashboard? Enter the new one here."
                      : 'A secret is already saved for this app. Click Change only if you need to rotate it.'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#706E6B] mb-1">Webhook Verify Token</label>
                <input
                  className={INPUT}
                  value={form.webhookVerifyToken}
                  onChange={(e) => setForm((f) => ({ ...f, webhookVerifyToken: e.target.value }))}
                  onBlur={(e) => setForm((f) => ({ ...f, webhookVerifyToken: trim(e.target.value) }))}
                  placeholder="a string you choose"
                />
                <p className="text-[11px] text-[#5C5C5C] mt-1">Enter this exact same value in the Webhooks product's Verify Token field.</p>
              </div>
              <button
                onClick={saveCredentials}
                disabled={saving || !form.appId || !form.webhookVerifyToken || (!form.appSecret && !hasCredential)}
                className="w-full bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2.5 rounded-[4px]"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {saved && <p className="text-[12px] text-[#2E844A] flex items-center gap-1"><CheckCircle size={13} /> Saved</p>}
            </div>

            {hasCredential && (
              <div className="border-t border-[#E5E5E5] pt-4">
                <p className="text-[12px] font-bold text-[#2E2E2E] mb-3">3. Connect your Page</p>
                <button
                  onClick={connect}
                  disabled={connecting}
                  className="w-full flex items-center justify-center gap-2 border border-[#E5E5E5] hover:border-[#1877F2] rounded-[4px] px-4 py-3 text-[13px] font-semibold text-[#2E2E2E] transition-colors disabled:opacity-60"
                >
                  <Facebook size={16} className="text-[#1877F2]" />
                  {connecting ? 'Redirecting…' : 'Connect with Facebook'}
                </button>

                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={() => setShowTokenConnect((v) => !v)}
                    className="text-[12px] font-semibold text-[#0176D3] hover:underline"
                  >
                    {showTokenConnect ? 'Hide' : "Redirect not working? Connect with an access token instead"}
                  </button>
                </div>

                {showTokenConnect && (
                  <div className="mt-3 border-t border-dashed border-[#E5E5E5] pt-3 space-y-2">
                    <p className="text-[11px] text-[#5C5C5C]">
                      Generate a <strong>User Access Token</strong> for your own app at{' '}
                      <a href="https://developers.facebook.com/tools/explorer" target="_blank" rel="noreferrer" className="underline font-semibold text-[#0176D3]">
                        Graph API Explorer
                      </a>{' '}
                      with the scopes <code>pages_show_list</code>, <code>pages_read_engagement</code>,{' '}
                      <code>pages_read_user_content</code>, <code>pages_manage_engagement</code>, <code>pages_manage_posts</code>,{' '}
                      <code>pages_manage_metadata</code>, <code>pages_messaging</code>, <code>business_management</code>, then paste it below. This bypasses
                      the OAuth redirect entirely — useful while App Domains is still being configured. The token is
                      exchanged server-side for your own long-lived token; nothing is stored beyond that.
                    </p>
                    <input
                      type="password"
                      className={INPUT}
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value)}
                      onBlur={(e) => setTokenInput(e.target.value.trim())}
                      placeholder="Paste your access token here"
                    />
                    <button
                      onClick={connectWithToken}
                      disabled={tokenConnecting || !tokenInput.trim()}
                      className="w-full bg-[#0176D3] hover:bg-[#0153A0] disabled:opacity-50 text-white text-[13px] font-semibold px-4 py-2.5 rounded-[4px]"
                    >
                      {tokenConnecting ? 'Connecting…' : 'Connect with This Token'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </Section>
      )}

      {accounts.length > 0 && (
        <Section title="Automation" icon={Zap}>
          <p className="text-[13px] text-[#5C5C5C]">
            Configure auto-reply rules for comments and DMs — including rules scoped to a specific
            post — from{' '}
            <button onClick={() => navigate('/dashboard/facebook/automation')} className="underline font-semibold text-[#0176D3]">
              Automation Rules
            </button>.
          </p>
        </Section>
      )}
    </div>
  );
}
